import {
  PLATFORM_AUTH_HANDOFF_ACK,
  PLATFORM_AUTH_HANDOFF_READY,
} from "@prismancy/shared";
import { useEffect, useState } from "react";
import { appRoutes } from "../../routes/appRoutes";
import {
  APP_LOGIN_ORIGIN,
  AUTH_TOKEN_KEY,
  LEGACY_DOMAIN_MIGRATION_ENABLED,
  LEGACY_PLATFORM_ORIGIN,
  SELECTED_WORKSPACE_KEY,
  USER_DETAILS_KEY,
} from "../../constants";
import { StorageService } from "../../services";
import {
  getAuthHandoffRedirect,
  isAuthHandoffLogoutRequest,
  isAuthHandoffRequest,
  isAuthHandoffSession,
  isTopLevelAuthHandoff,
} from "./authHandoffPolicy";
import { getAuthHandoffSenderPolicy } from "./legacyDomainMigrationPolicy";

const AuthHandoffReceiver = () => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const opener = window.opener && !window.opener.closed ? window.opener : null;
    const sender = opener ?? (window.parent !== window ? window.parent : null);
    const usesTopLevelSession = isTopLevelAuthHandoff(
      window.location.search,
      window.parent === window,
    );
    const { origin: senderOrigin, legacyRequestId } = getAuthHandoffSenderPolicy(
      window.location.search,
      APP_LOGIN_ORIGIN,
      LEGACY_PLATFORM_ORIGIN,
      LEGACY_DOMAIN_MIGRATION_ENABLED,
    );
    const usesLegacySender = legacyRequestId !== null;

    if (usesTopLevelSession) {
      let active = true;
      const redirect = getAuthHandoffRedirect(
        window.location.search,
        window.location.origin,
      );

      const syncTopLevelSession = async () => {
        try {
          const response = await fetch(`${APP_LOGIN_ORIGIN}/api/session?validate=true`, {
            credentials: "include",
            cache: "no-store",
            headers: { Accept: "application/json" },
          });
          if (response.status === 401) {
            const loginUrl = new URL("/", APP_LOGIN_ORIGIN);
            const requestedRedirect = new URLSearchParams(window.location.search).get(
              "redirect",
            );
            if (requestedRedirect) {
              loginUrl.searchParams.set("redirect", requestedRedirect);
            }
            window.location.replace(loginUrl.toString());
            return;
          }
          if (!response.ok) throw new Error("Session unavailable");

          const session: unknown = await response.json();
          if (!isAuthHandoffSession(session)) {
            throw new Error("Invalid session payload");
          }

          const workspaceId = session.active_workspace?.[0]?.id;
          if (workspaceId) {
            await StorageService.setStorageItem(SELECTED_WORKSPACE_KEY, workspaceId);
          } else {
            await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY);
          }
          await StorageService.setStorageItem(AUTH_TOKEN_KEY, session.access_token);
          if (active) window.location.replace(redirect);
        } catch {
          if (active) setFailed(true);
        }
      };

      void syncTopLevelSession();
      return () => {
        active = false;
      };
    }

    if (!sender) {
      setFailed(true);
      return;
    }

    const handleMessage = async (event: MessageEvent<unknown>) => {
      if (event.origin !== senderOrigin || event.source !== sender) return;
      const isLogin = isAuthHandoffRequest(event.data);
      const isLogout = isAuthHandoffLogoutRequest(event.data);
      if (!isLogin && !isLogout) return;
      if (usesLegacySender && event.data.requestId !== legacyRequestId) return;

      let success = false;
      try {
        if (isLogin) {
          if (event.data.workspaceId) {
            await StorageService.setStorageItem(
              SELECTED_WORKSPACE_KEY,
              event.data.workspaceId,
            );
          } else {
            await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY);
          }
          // Write the token last so other tabs only react after the workspace is ready.
          await StorageService.setStorageItem(AUTH_TOKEN_KEY, event.data.token);
        } else {
          await StorageService.removeStorageItem(SELECTED_WORKSPACE_KEY);
          await StorageService.removeStorageItem(USER_DETAILS_KEY);
          // Remove the token last; it is the cross-tab logout signal.
          await StorageService.removeStorageItem(AUTH_TOKEN_KEY);
        }
        success = true;
      } finally {
        sender.postMessage(
          {
            type: PLATFORM_AUTH_HANDOFF_ACK,
            requestId: event.data.requestId,
            success,
          },
          senderOrigin,
        );
        if (success) window.removeEventListener("message", handleMessage);
        if (success && isLogin && opener) {
          window.location.replace(appRoutes.experiments.root);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    sender.postMessage(
      {
        type: PLATFORM_AUTH_HANDOFF_READY,
        ...(usesLegacySender ? { requestId: legacyRequestId } : {}),
      },
      senderOrigin,
    );
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return (
    <main
      aria-label="Completing login"
      className="flex h-full w-full items-center justify-center bg-background-surface-01 px-6"
    >
      {failed ? (
        <a
          href={`${APP_LOGIN_ORIGIN}/`}
          className="text-sm text-button-link hover:text-text-accent"
        >
          Return to sign in
        </a>
      ) : null}
    </main>
  );
};

export default AuthHandoffReceiver;
